import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { APP_VERSION } from '../../constants/app-version';
import { PosthogService } from './posthog.service';

/** Une même erreur n'est rapportée qu'une fois par cette durée. */
export const ERROR_DEDUP_WINDOW_MS = 5 * 60 * 1000;
/** Plafond d'erreurs rapportées par visite : une boucle d'erreurs ne doit pas inonder PostHog. */
export const ERROR_MAX_PER_SESSION = 25;

const MAX_MESSAGE_LENGTH = 300;
const MAX_STACK_LENGTH = 1500;

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const EMAIL = /[^\s@'"<>()]+@[^\s@'"<>()]+\.[a-z]{2,}/gi;
const JWT = /eyJ[\w-]+\.[\w-]+\.[\w-]*/g;
const LONG_TOKEN = /\b[A-Za-z0-9_-]{32,}\b/g;

/** Retire de ce qu'on envoie tout ce qui identifie une personne ou donne accès à un compte. */
export function scrubPersonalData(text: string): string {
  return text
    .replace(JWT, '[token]')
    .replace(EMAIL, '[email]')
    .replace(UUID, '[id]')
    .replace(LONG_TOKEN, '[token]');
}

/** Route sans paramètres de requête ni identifiants : `/garden/plants/<uuid>?x=1` devient `/garden/plants/:id`. */
export function routeTemplate(url: string): string {
  const path = url.split(/[?#]/)[0];
  return path
    .split('/')
    .map((segment) => (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(segment) || /^\d{5,}$/.test(segment) ? ':id' : segment))
    .join('/');
}

/**
 * Rend visibles en production les erreurs du front que personne ne voyait : tout ce
 * qui remonte jusqu'à Angular (exception d'un gabarit, promesse rejetée) part vers
 * PostHog (suivi d'erreurs) avec la version du build et la route, sans donnée personnelle.
 *
 * L'erreur reste affichée dans la console comme avec le gestionnaire par défaut.
 * Rapporter ne doit jamais casser l'application : tout échec ici est avalé.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly lastSentByKey = new Map<string, number>();
  private sentCount = 0;

  constructor(private injector: Injector) {}

  handleError(error: unknown): void {
    console.error(error);
    try {
      this.report(error);
    } catch {
      // un rapport défaillant ne doit pas produire une seconde erreur
    }
  }

  private report(error: unknown): void {
    // Angular enveloppe les promesses rejetées dans `{ rejection }`.
    const raw = (error as { rejection?: unknown } | null)?.rejection ?? error;
    const name = raw instanceof Error ? raw.name : typeof raw;
    const message = scrubPersonalData(this.messageOf(raw)).slice(0, MAX_MESSAGE_LENGTH);
    const stack = scrubPersonalData(raw instanceof Error ? (raw.stack ?? '') : '').slice(0, MAX_STACK_LENGTH);

    if (!this.shouldSend(`${name}|${message}|${stack.split('\n')[1] ?? ''}`)) return;

    const sanitized = new Error(message);
    sanitized.name = name;
    sanitized.stack = stack;
    this.injector.get(PosthogService).captureException(sanitized, {
      app_version: APP_VERSION,
      route: routeTemplate(this.injector.get(Router).url),
    });
  }

  private messageOf(raw: unknown): string {
    if (raw instanceof Error) return raw.message;
    if (typeof raw === 'string') return raw;
    return 'Erreur non typée';
  }

  /** Déduplication par erreur (fenêtre glissante) puis plafond par visite. */
  private shouldSend(key: string): boolean {
    const now = Date.now();
    const last = this.lastSentByKey.get(key);
    if (last !== undefined && now - last < ERROR_DEDUP_WINDOW_MS) return false;
    if (this.sentCount >= ERROR_MAX_PER_SESSION) return false;
    this.lastSentByKey.set(key, now);
    this.sentCount++;
    return true;
  }
}
